/**
 * Backward flow, stage 1 — the per-IR-node serializer (model → text).
 *
 * Prints a resolved {@link ProcessModel} / {@link SubprocessModel} back to
 * canonical `.arch` process text. It is the inverse of parse+build: the IR
 * carries verbatim text (callee paths, conditions, heads, args, `select` counts,
 * boolean merge expressions) and the owner's *syntactic* form (explicit / `|`
 * ditto / inherited / `dist` / none) precisely so this round-trips —
 * `text → IR → text → IR` is the IR identity (fuzzed in process-roundtrip.test).
 *
 * This is NOT the whole-document formatter (`format-source.ts`): that re-indents
 * a whole file from one TextEdit. This prints a single process subtree, so a
 * future minimal-diff patcher can splice one node's text without touching the
 * rest of the document.
 *
 * Owner printing is driven by `Owner.form`, not `resolved`: an `inherited` /
 * `none` owner was written with NO prefix (omitted / anonymous head) and must
 * print none, else the round-trip would inject explicit owners everywhere.
 */

import type {
  ProcessModel,
  SubprocessModel,
  Flow,
  ProcessNode,
  Owner,
  CallNode,
  BlockNode,
  SelectCount,
  LoopHead,
  MergeClause,
  BoolExpr,
  ReversibleItem,
  UnwindAction,
} from "./process-model.js";
import { RESERVED_KEYWORDS } from "./lexer.js";
import { exprText } from "./selector-ast.js";

const INDENT = "    "; // 4 spaces (examples convention)

function pad(depth: number): string {
  return INDENT.repeat(depth);
}

/** Re-escape a decoded string for a `"…"` literal. The lexer un-escapes both
 *  `\"` → `"` and `\\` → `\`, so the exact inverse escapes both: every `"` and
 *  every `\` is prefixed with a backslash. Escaping `\` matters for a value
 *  ending in one — without it the closing quote would re-lex as `\"`
 *  (an escaped quote) and the string would run unterminated. */
function esc(s: string): string {
  return s.replace(/[\\"]/g, "\\$&");
}

/** A quoted string literal (notes, conditions, reasons, triggers). */
function q(s: string): string {
  return `"${esc(s)}"`;
}

/** A plain identifier that is NOT a reserved keyword — the only value that
 *  re-lexes to a single IDENTIFIER token. */
function isBareIdent(s: string): boolean {
  return /^[A-Za-z_]\w*$/.test(s) && !RESERVED_KEYWORDS.has(s);
}

/** A note step's optional `{ latency: … }` field body (latency analysis). */
function noteBody(fields: readonly import("./types.js").FieldDecl[] | undefined): string {
  if (!fields || fields.length === 0) return "";
  const entries = fields.map((f) => `${f.path.join(".")}: ${valueLit(f.value)}`);
  return ` { ${entries.join("; ")} }`;
}

/** Every `Value` kind is spelled out and there is deliberately NO `default`:
 *  exhaustiveness is what makes the compiler point here when the value grammar
 *  grows. A `default` returning `""` silently drops the value instead, and the
 *  reprint then does not even re-parse (`latency:` with nothing after it). */
function valueLit(v: import("./types.js").Value | undefined): string {
  if (!v) return "";
  switch (v.kind) {
    case "identifier":
      return v.text;
    case "ref":
      return v.path.join(".") + (v.instance ? ` as ${v.instance}` : "");
    case "string":
      return q(v.text) + (v.instance ? ` as ${v.instance}` : "");
    case "number":
      return String(v.value);
    case "boolean":
      return String(v.value);
    case "list":
      return v.items.map(valueLit).join(", ");
    case "expr":
      // §4.3.2: a note's field body accepts any value, so it can hold a getter
      // or a cast. The post-cascade resolver walks MODULE fields, so a process
      // step's field is never resolved — the expression is still here at print
      // time and prints as its source form.
      return exprText(v.expr);
  }
}

/** A head / case / catch / trigger / loop-iterator label. Bare ONLY for a
 *  plain non-keyword identifier — a keyword (would re-lex as a keyword: `one`
 *  flips a select count, `try` flips a loop to retry), a number (re-lexes as a
 *  count/number), a dotted name, or anything with spaces MUST be quoted (STRING
 *  is accepted in every such position, so quoting always round-trips). */
function headLit(s: string): string {
  return isBareIdent(s) ? s : q(s);
}

/** A loop collection (`each x in <coll>`). Accepts a qualified name — so a
 *  dotted path of non-keyword identifiers stays bare (`order.items`); anything
 *  else (keyword segment, spaces) is quoted. */
function collLit(s: string): string {
  const segs = s.split(".");
  return segs.length > 0 && segs.every(isBareIdent) ? s : q(s);
}

/** `await` target — free-form words (`1 day`, `until ready`) stay bare; quote
 *  when it contains a `"` or any word that re-lexes as a reserved keyword
 *  (`await go now` would not parse, since the free-form scan stops at `go`). */
function awaitText(s: string): string {
  const wordSafe = /^[\w][\w\s]*$/.test(s) && !s.split(/\s+/).some((w) => RESERVED_KEYWORDS.has(w));
  return wordSafe ? s : q(s);
}

/** The written owner prefix for an action, WITHOUT a trailing space.
 *  `""` when the owner was omitted (inherited) or unresolved (none). */
function ownerPrefix(owner: Owner | undefined): string {
  if (!owner) return "";
  switch (owner.form) {
    case "explicit":
      return owner.resolved ? owner.resolved.join(".") : "";
    case "ditto":
      return "|";
    case "snake":
      return "\\";
    case "dist":
      return "dist";
    case "inherited":
    case "none":
      return "";
  }
}

/** Prefix + a trailing space when non-empty (`"Orders "`, `""`). */
function ownerLead(owner: Owner | undefined): string {
  const p = ownerPrefix(owner);
  return p ? `${p} ` : "";
}

function selectCountText(c: SelectCount): string {
  if (c.kind === "one") return "one";
  if (c.kind === "n") return String(c.n);
  return ""; // all
}

function loopHeadText(h: LoopHead | undefined): string {
  if (!h) return "";
  const label = h.label ? headLit(h.label.text) : "";
  const coll = h.collection ? ` in ${collLit(h.collection.text)}` : "";
  return `${label}${coll}`.trim();
}

function boolText(e: BoolExpr): string {
  if (e.kind === "name") return e.name;
  return e.terms
    .map((t) => (t.kind !== "name" && t.kind !== e.kind ? `(${boolText(t)})` : boolText(t)))
    .join(` ${e.kind} `);
}

function mergeText(m: MergeClause): string {
  const parts: string[] = [];
  if (m.detached) parts.push("out");
  if (m.policy) parts.push(m.policy);
  if (m.threshold) {
    if (m.threshold.kind === "count") parts.push(String(m.threshold.n));
    else parts.push(m.threshold.text?.text || boolText(m.threshold.expr));
  }
  return parts.length ? ` ${parts.join(" ")}` : "";
}

function argsText(args: readonly { text: string }[] | undefined): string {
  if (!args) return ""; // an empty arg list `()` was written → keep it (args = [])
  return `(${args.map((a) => a.text).join(", ")})`;
}

/** A call/chain on one line: `[bind = ][owner ]> a.b(args) > c.d ["note"] [as name]`. */
function callLine(n: CallNode): string {
  const bind = n.bind ? `${n.bind.name} = ` : "";
  const lead = ownerLead(n.owner);
  const hops = n.hops.map((h) => `> ${h.callee.text.text}${argsText(h.args)}`).join(" ");
  const comment = n.comment ? ` ${q(n.comment.text)}` : "";
  const asName = n.asName ? ` as ${n.asName.name}` : "";
  return `${bind}${lead}${hops}${comment}${asName}`.trimEnd();
}

export function unwindActionText(a: UnwindAction, depth: number): string {
  switch (a.kind) {
    case "call":
      return callLine(a);
    case "terminator": {
      const kw = a.all ? `${a.outcome} all` : a.outcome;
      return a.reason ? `${kw} ${q(a.reason.text)}` : kw;
    }
    case "splice":
      return spliceText(a);
    case "block":
      return blockText(a, depth);
  }
}

function spliceText(n: { target: readonly string[]; templateCaller?: Owner; args?: readonly { text: string }[]; asName?: { name: string }; comment?: { text: string } }): string {
  const caller = n.templateCaller ? ownerLead(n.templateCaller) : "";
  const asName = n.asName ? ` as ${n.asName.name}` : "";
  const comment = n.comment ? ` ${q(n.comment.text)}` : "";
  return `${caller}do ${n.target.join(".")}${argsText(n.args)}${asName}${comment}`.trimStart();
}

/** `{ … }` — body on its own indented lines. Empty → `{}`. */
function blockText(b: BlockNode, depth: number): string {
  if (!b.body || b.body.length === 0) return "{}";
  // A parser-synthesized chain wrapper is a LINE, not a body: printing braces
  // around it would re-parse as an authored block, which §4.13.4 rule 1 makes a
  // boundary — silently cutting a `\` run and re-pointing a following `|` at a
  // step inside the braces. Print the hops back as the one line they were.
  if (b.chain) return printFlow(b.body, depth).trimStart();
  const inner = printFlow(b.body, depth + 1);
  return `{\n${inner}\n${pad(depth)}}`;
}

/** A flow body as `{ … }` (or `{}`). */
function bodyBlock(body: Flow | undefined, depth: number): string {
  if (!body || body.length === 0) return "{}";
  return `{\n${printFlow(body, depth + 1)}\n${pad(depth)}}`;
}

/** Print one IR node at the given indent depth (leading pad included). The
 *  backward-flow patcher uses this to re-serialize a single (possibly mutated)
 *  node for a surgical span replacement. */
export function printNode(n: ProcessNode | BlockNode, depth: number): string {
  const ind = pad(depth);
  switch (n.kind) {
    case "call":
      return ind + callLine(n);
    case "note":
      return `${ind}${ownerLead(n.owner)}${q(n.text.text)}${noteBody(n.fields)}`;
    case "prose":
      return ind + q(n.text.text);
    case "description":
      return `${ind}${q(n.text.text)}`;
    case "terminator": {
      const kw = n.all ? `${n.outcome} all` : n.outcome;
      return ind + (n.reason ? `${kw} ${q(n.reason.text)}` : kw);
    }
    case "reentry":
      return `${ind}go ${n.target.name}${n.comment ? ` ${q(n.comment.text)}` : ""}`;
    case "splice":
      return ind + spliceText(n);
    case "await":
      return `${ind}${ownerLead(n.owner)}await ${awaitText(n.target.text)}`;
    case "block":
      return ind + blockText(n, depth);
    case "owner_block":
      return `${ind}${ownerLead(n.owner)}${bodyBlock(n.body, depth)}`;
    case "branch": {
      // `if [cond] [body] (else if …)* (else …)?` — arms after the first chain
      // onto the previous close brace. A condition-only arm (no body) is a draft.
      let out = `${ind}${ownerLead(n.owner)}if`;
      out += n.arms[0]!.condition ? ` ${q(n.arms[0]!.condition.text)}` : "";
      out += n.arms[0]!.body ? ` ${bodyBlock(n.arms[0]!.body, depth)}` : "";
      for (let i = 1; i < n.arms.length; i++) {
        const arm = n.arms[i]!;
        out += ` else if${arm.condition ? ` ${q(arm.condition.text)}` : ""}`;
        out += arm.body ? ` ${bodyBlock(arm.body, depth)}` : "";
      }
      // A body-less `else` is a draft — print bare `else` (not `else {}`, which
      // would re-parse to an explicit empty block).
      if (n.elseArm) out += n.elseArm.body ? ` else ${bodyBlock(n.elseArm.body, depth)}` : " else";
      return out;
    }
    case "select": {
      const count = selectCountText(n.count);
      const head = n.head ? ` ${headLit(n.head.text)}` : "";
      const hdr = `${ownerLead(n.owner)}select${count ? ` ${count}` : ""}${head}`;
      const cases = n.cases
        .map((c) => `${pad(depth + 1)}${headLit(c.label.text)} ${bodyBlock(c.body, depth + 1)}`)
        .join("\n");
      return `${ind}${hdr} {\n${cases}\n${ind}}`;
    }
    case "loop": {
      const head = loopHeadText(n.head);
      return `${ind}${ownerLead(n.owner)}each${head ? ` ${head}` : ""} ${bodyBlock(n.body, depth)}`;
    }
    case "retry": {
      const head = loopHeadText(n.bound);
      let out = `${ind}${ownerLead(n.owner)}each${head ? ` ${head}` : ""} try ${bodyBlock(n.attempt, depth)}`;
      for (const c of n.catches) out += ` catch${c.label ? ` ${headLit(c.label.text)}` : ""} ${bodyBlock(c.body, depth)}`;
      if (n.elseBody) out += ` else ${bodyBlock(n.elseBody, depth)}`;
      return out;
    }
    case "guard": {
      let out = `${ind}${ownerLead(n.owner)}try ${bodyBlock(n.attempt, depth)}`;
      for (const c of n.catches) out += ` catch${c.label ? ` ${headLit(c.label.text)}` : ""} ${bodyBlock(c.body, depth)}`;
      return out;
    }
    case "parallel": {
      const awaitKw = n.awaitClause
        ? ` await ${awaitText(n.awaitClause.head.text)}${n.awaitClause.asName ? ` as ${n.awaitClause.asName.text}` : ""}`
        : "";
      const hdr = `${ownerLead(n.owner)}parallel${mergeText(n.merge)}${awaitKw}`;
      const elseSuffix = n.awaitClause?.elseBody ? ` else ${bodyBlock(n.awaitClause.elseBody, depth)}` : "";
      if (n.body.form === "fanOut") {
        const head = loopHeadText(n.body.head);
        return `${ind}${hdr} each${head ? ` ${head}` : ""} ${bodyBlock(n.body.body, depth)}`;
      }
      // v0.9.0 §2.3: a single implicit branch prints without the `branch`
      // keyword — the body's steps sit directly inside the braces.
      if (n.body.form === "branches" && n.body.branches.length === 1 && n.body.branches[0]!.implicit) {
        return `${ind}${hdr} ${bodyBlock(n.body.branches[0]!.body, depth)}${elseSuffix}`;
      }
      const selectKw = n.body.form === "select" ? ` select${selectCountText(n.body.count) ? ` ${selectCountText(n.body.count)}` : ""}` : "";
      const branches = n.body.branches
        .map((b) => `${pad(depth + 1)}branch${b.name ? ` ${b.name.name}` : ""} ${bodyBlock(b.body, depth + 1)}`)
        .join("\n");
      return `${ind}${hdr}${selectKw} {\n${branches}\n${ind}}${elseSuffix}`;
    }
    case "reversible": {
      const trig = n.trigger.explicit && n.trigger.text ? ` on ${headLit(n.trigger.text.text)}` : "";
      const items = n.items.map((it) => `${pad(depth + 1)}${reversibleItemText(it, depth + 1)}`).join("\n");
      return `${ind}${ownerLead(n.owner)}reversible${trig} {\n${items}\n${ind}}`;
    }
  }
}

function reversibleItemText(it: ReversibleItem, depth: number): string {
  let out = "";
  if (it.forward) {
    // Forward prints inline (its own indent stripped — the item line owns it).
    out = printNode(it.forward, depth).trimStart();
  }
  if (it.unwind) out += `${out ? " " : ""}unwind ${unwindActionText(it.unwind.action, depth)}`;
  if (it.asName) out += ` as ${it.asName.name}`;
  return out;
}

export function printFlow(flow: Flow, depth: number): string {
  return flow.map((n) => printNode(n, depth)).join("\n");
}

/** Body lines for a process/subprocess at `depth`: a leading `"description"`
 *  docstring (if any — a bare string in a process body IS the description) then
 *  the flow. So the description survives the round-trip. */
function bodyLines(model: ProcessModel | SubprocessModel, depth: number): string {
  const parts: string[] = [];
  if (model.description) parts.push(`${pad(depth)}${q(model.description)}`);
  const flow = printFlow(model.body, depth);
  if (flow) parts.push(flow);
  return parts.join("\n");
}

/** Print a process body (no `process <name> { }` wrapper — just the steps). */
export function printProcessBody(model: ProcessModel | SubprocessModel): string {
  return bodyLines(model, 0);
}

/** Print a full `process <name> { … }` declaration. */
export function printProcess(model: ProcessModel): string {
  if (model.anonymous) return bodyLines(model, 0);
  const exp = model.exported ? "export " : "";
  const id = model.id ? `#${model.id} ` : "";
  return `${exp}process ${id}${model.name} {\n${bodyLines(model, 1)}\n}`;
}

/** Print a full `subprocess <name>[ on Caller] { … }` declaration. */
export function printSubprocess(model: SubprocessModel): string {
  const exp = model.exported ? "export " : "";
  const id = model.id ? `#${model.id} ` : "";
  const params = model.params.length ? `(${model.params.map((p) => p.name).join(", ")})` : "";
  const on = model.templateParam ? ` on ${model.templateParam.name}` : "";
  return `${exp}subprocess ${id}${model.name}${params}${on} {\n${bodyLines(model, 1)}\n}`;
}
